// useTasks — the band-scoped universal-task hook set, built on
// useBandQuery/useBandMutation so the band-scoped keys are ALWAYS ['band', bandId, …] (the tenant in the key,
// the structural fix). The task list feeds the dashboard widget + the drawer; the frozen `task-changed`
// signal invalidates ['band', bandId, 'tasks'] + ['me','tasks'] via the invalidationRegistry.
//
// Optimistic rules: status TRANSITION and ASSIGN are in the proven-safe optimistic set (onMutate
// snapshot → rollback+toast onError → invalidate onSettled). Create / update / delete / comment are
// server-confirm THEN repaint (create/comment show a pending state). Every mutation surfaces the server error
// verbatim and NEVER reports success on an unpersisted write (the CRIT-111 rule).
import { useQueryClient, useQuery, type QueryClient } from "@tanstack/react-query";
import type { components } from "@cntrl/api-client";
import { useApp } from "../appContext.js";
import { useBandContext } from "../bandContext.js";
import { useMe } from "./useSession.js";
import { useBandQuery, useBandMutation } from "./useBandQuery.js";
import { ApiError, errorMessage } from "./apiError.js";
import { pushToast } from "../toasts.js";

type TaskListRead = components["schemas"]["TaskListRead"];
type TaskSummary = components["schemas"]["TaskSummary"];
type TaskDetail = components["schemas"]["TaskDetail"];
type MyTasksRead = components["schemas"]["MyTasksRead"];
type MyTaskDto = components["schemas"]["MyTaskDto"];
type SaveTaskRequest = components["schemas"]["SaveTaskRequest"];
type UpdateTaskRequest = components["schemas"]["UpdateTaskRequest"];
type TransitionRequest = components["schemas"]["TransitionRequest"];
type AssignRequest = components["schemas"]["AssignRequest"];
type TaskAssigneeDto = components["schemas"]["TaskAssigneeDto"];
type TaskCommentDto = components["schemas"]["TaskCommentDto"];
type TaskActivityDto = components["schemas"]["TaskActivityDto"];
type TaskLink = components["schemas"]["TaskLink"];
type TaskSubtask = components["schemas"]["TaskSubtask"];
type TaskSubtaskInput = components["schemas"]["TaskSubtaskInput"];

export type {
  TaskListRead,
  TaskSummary,
  TaskDetail,
  MyTasksRead,
  MyTaskDto,
  SaveTaskRequest,
  UpdateTaskRequest,
  TransitionRequest,
  AssignRequest,
  TaskAssigneeDto,
  TaskCommentDto,
  TaskActivityDto,
  TaskLink,
  TaskSubtask,
  TaskSubtaskInput,
};

// ---- status vocabulary ----------------------------------------------------------

/**
 * The task status vocabulary — the fixed UI presentation taxonomy mirroring the server's `TaskStatuses`
 * (`Modules/Artist/TaskModels.cs`). Presentation labels + a `tone` key that drives the colored STATUS PILL
 * (data-tone in the SCSS). Not a business value — the transition RULES are server-enforced.
 */
export const TASK_STATUSES = [
  "open",
  "in_progress",
  "blocked",
  "needs_review",
  "revisions_requested",
  "approved",
  "done",
] as const;
export type TaskStatus = (typeof TASK_STATUSES)[number];

/**
 * The free-text ceilings, mirroring `TaskLimits` server-side (which is authoritative and 422s past them).
 * They live here so an input can stop at the limit instead of letting someone type a paragraph the save will
 * refuse. Not a business value — a storage bound.
 */
export const TASK_TITLE_MAX = 500;
export const TASK_NOTES_MAX = 4000;

// `open` reads "Open", the spec's own word for it — and the word the status token already is. It used to
// read "Not started", which is the label for `not_started`: a status the release-checklist kind had and this
// unified model does not. One funnel, one vocabulary, so the drawer's pills and the activity feed agree.
const STATUS_LABELS: Record<string, string> = {
  open: "Open",
  in_progress: "In progress",
  blocked: "Blocked",
  needs_review: "Needs review",
  revisions_requested: "Revisions requested",
  approved: "Approved",
  done: "Done",
};

/** The human label for a status token (falls back to a title-cased raw value for an unknown server status). */
export function statusLabel(status: string | null | undefined): string {
  if (!status) return "Open";
  return STATUS_LABELS[status] ?? status.replace(/_/g, " ");
}

/**
 * The STATUS PILL tone key (drives `[data-tone]` in the SCSS → the colored pill): gray OPEN · blue IN PROGRESS
 * · purple NEEDS REVIEW · green DONE/APPROVED · amber REVISIONS REQUESTED · red BLOCKED.
 */
export function statusTone(status: string | null | undefined): string {
  switch (status) {
    case "in_progress":
      return "in_progress";
    case "blocked":
      return "blocked";
    case "needs_review":
      return "needs_review";
    case "revisions_requested":
      return "revisions_requested";
    case "approved":
    case "done":
      return "done";
    default:
      return "open";
  }
}

/** True when a task is past due AND not in a terminal-good state (drives the row's danger treatment). */
export function isOverdue(dueDate: string | null | undefined, status: string | null | undefined, today = new Date()): boolean {
  if (!dueDate) return false;
  if (status === "approved" || status === "done") return false;
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(dueDate);
  if (!m) return false;
  // Wall-clock comparison — the client never tz-converts a date-only value.
  const [, y, mo, d] = m;
  const due = new Date(Number(y), Number(mo) - 1, Number(d));
  const todayMidnight = new Date(today.getFullYear(), today.getMonth(), today.getDate());
  return due < todayMidnight;
}

/**
 * True when a task falls due within the next few days — INCLUDING today — and is not already in a
 * terminal-good state. Drives the row's "due soon" tint, one step below `isOverdue`'s danger treatment.
 *
 * WALL-CLOCK at BOTH ends, like its sibling above: the client never tz-converts a date-only value, and
 * `today` is taken at midnight so a task due today is due-soon for the whole day. The roster widget's own
 * copy compared the date against `today` carrying its time of day, so a task due today stopped being "soon"
 * a second after midnight and then carried NEITHER tint — it is not overdue until tomorrow. Consolidated
 * here (ART-131) when the release checklist became the third caller: a rule with three copies holds two
 * opinions, which is what `holdCountdown` already cost us.
 *
 * `days` is a presentation threshold, not a business value — how much runway reads as "soon" on a row.
 */
export function isDueSoon(
  dueDate: string | null | undefined,
  status: string | null | undefined,
  today = new Date(),
  days = 3,
): boolean {
  if (!dueDate) return false;
  if (status === "approved" || status === "done") return false;
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(dueDate);
  if (!m) return false;
  const [, y, mo, d] = m;
  const due = new Date(Number(y), Number(mo) - 1, Number(d));
  const from = new Date(today.getFullYear(), today.getMonth(), today.getDate());
  const to = new Date(from);
  to.setDate(to.getDate() + days);
  return due >= from && due <= to;
}

const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

/** Format a YYYY-MM-DD wall-clock due date to "Aug 8" (no year) without any timezone shift. Blank in → "". */
export function formatDueDate(date: string | null | undefined): string {
  if (!date) return "";
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(date);
  if (!m) return date;
  const [, , mo, d] = m;
  return `${MONTHS[Number(mo) - 1] ?? mo} ${Number(d)}`;
}

/** A release's drop date for the drawer's header ("Sep 30, 2026"). Carries the YEAR, unlike a due date: a
 *  rollout can sit against next year's release and "Sep 30" alone would be ambiguous. Wall-clock, like its
 *  sibling — the client never tz-converts a date-only value. */
export function formatDropDate(date: string | null | undefined): string {
  if (!date) return "";
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(date);
  if (!m) return date;
  const [, y, mo, d] = m;
  return `${MONTHS[Number(mo) - 1] ?? mo} ${Number(d)}, ${y}`;
}

// ---- transition state machine (mirrors the server) -----------------------------------------------

/** One offered transition button: its human label and the status it moves the task TO. */
export interface TaskAction {
  label: string;
  toStatus: TaskStatus;
}

// The ADVANCE transitions the assignee (or initiator / an AV) walks — the exact set the server permits
// (TaskModels.cs `AdvanceTransitions`). Keyed by the CURRENT status. The server re-validates (422 on illegal).
const ADVANCE_ACTIONS: Record<string, TaskAction[]> = {
  open: [{ label: "Start", toStatus: "in_progress" }],
  in_progress: [
    { label: "Submit for review", toStatus: "needs_review" },
    { label: "Mark done", toStatus: "done" },
    { label: "Block", toStatus: "blocked" },
  ],
  blocked: [{ label: "Unblock", toStatus: "in_progress" }],
  needs_review: [{ label: "Pull back", toStatus: "in_progress" }],
  revisions_requested: [{ label: "Resume", toStatus: "in_progress" }],
  approved: [{ label: "Mark done", toStatus: "done" }],
  done: [],
};

// The INITIATOR-ONLY approval transitions (TaskModels.cs `ApproverTransitions`) — only from `needs_review`.
const APPROVER_ACTIONS: Record<string, TaskAction[]> = {
  needs_review: [
    { label: "Approve", toStatus: "approved" },
    { label: "Request revisions", toStatus: "revisions_requested" },
  ],
};

/** The assignee-facing advance buttons for the current status (empty for a terminal status). */
export function advanceActions(status: string | null | undefined): TaskAction[] {
  return (status && ADVANCE_ACTIONS[status]) || [];
}

/** The initiator-only approval buttons for the current status (only non-empty at `needs_review`). */
export function approvalActions(status: string | null | undefined): TaskAction[] {
  return (status && APPROVER_ACTIONS[status]) || [];
}

// ---- query keys + rollback -----------------------------------------------------------------------------

const tasksPrefix = (bandId: string) => ["band", bandId, "tasks"] as const;

/**
 * The composed reads whose To-do cards carry this band's tasks under the roster lenses (ART-343): the per-artist
 * Label/Management dashboard, the Booking dashboard, and the org's cross-roster overview + to-do reads. Each is a
 * separate query from the task list, so invalidating the list alone left a deleted task on the roster's card.
 * The server busts its own copies of the same reads on every task write (TasksController).
 */
function invalidateTaskBoards(qc: QueryClient, bandId: string): void {
  void qc.invalidateQueries({ queryKey: ["band", bandId, "roster", "dashboard"] });
  void qc.invalidateQueries({ queryKey: ["band", bandId, "booking", "dashboard"] });
  void qc.invalidateQueries({
    predicate: (q) =>
      q.queryKey[0] === "org" && q.queryKey[2] === "roster"
      && (q.queryKey[3] === "overview" || q.queryKey[3] === "todos"),
  });
}
const taskKey = (bandId: string, taskId: string) => ["band", bandId, "task", taskId] as const;

/** Restore each snapshotted list query + the detail to their pre-mutation state (optimistic rollback). */
function restore(
  qc: QueryClient,
  detailKey: readonly unknown[],
  prevDetail: TaskDetail | undefined,
  prevLists: [readonly unknown[], unknown][],
): void {
  if (prevDetail) qc.setQueryData(detailKey, prevDetail);
  for (const [key, data] of prevLists) qc.setQueryData(key, data);
}

/** A task's optional filters: by status, by release, the caller's own assignments, or still-open only. */
export interface TaskFilters {
  status?: string;
  releaseId?: string;
  assignedToMe?: boolean;
  /** Everything not done or approved — what a TO-DO list means. Served by the `ix_tasks_widget` partial index. */
  openOnly?: boolean;
}

// ---- queries -------------------------------------------------------------------------------------------

/**
 * `GET /bands/{bandId}/tasks` — the band's tasks for the widget/spreadsheet. Keyed
 * ['band', bandId, 'tasks', filters]; a 403 (no band:read) surfaces as the query error. Org-lens reads are the
 * Phase-7 roster view — this hook is the band-shell widget.
 */
export function useTasks(filters: TaskFilters = {}) {
  const { api } = useApp();
  return useBandQuery<TaskListRead>(
    ["tasks", filters],
    async (bandId) => {
      const { data, error, response } = await api.GET("/api/v1/bands/{bandId}/tasks", {
        params: {
          path: { bandId },
          query: {
            status: filters.status,
            releaseId: filters.releaseId,
            assignedToMe: filters.assignedToMe,
            openOnly: filters.openOnly,
          },
        },
      });
      if (!response.ok || !data) throw new ApiError(response.status, error);
      return data;
    },
    { staleTime: 30_000 },
  );
}

/**
 * `GET /me/tasks` — the caller's assignments ACROSS all bands. Its one consumer is the mobile home
 * greeting's "N things need you" sub-line, which filters the cross-band list down to the active band
 * (the "Tasks for you" panel this was built for was cut, 2026-08-29 — owner ruling). PERSON-scoped
 * (['me','tasks'], immune to the band-scoped scorch), so it survives a band switch like ['me','opportunities'].
 * `task-changed` on the user scope invalidates it (invalidationRegistry). Lives in app/hooks (lint-exempt).
 */
export function useMyTasks() {
  const { api } = useApp();
  return useQuery<MyTasksRead, Error>({
    queryKey: ["me", "tasks"],
    queryFn: async () => {
      const { data, error, response } = await api.GET("/api/v1/me/tasks");
      if (!response.ok || !data) throw new ApiError(response.status, error);
      return data;
    },
    staleTime: 30_000,
  });
}

/**
 * `GET /bands/{bandId}/tasks/{taskId}` — one task's full detail (fields + assignees + comments +
 * activity), the drawer's source. Keyed ['band', bandId, 'task', taskId]; disabled for a create (`new`) or no
 * id. A 404 (deleted under us) surfaces as the query error → the drawer's "not available" state.
 */
export function useTaskDetail(taskId: string | undefined) {
  const { api } = useApp();
  return useBandQuery<TaskDetail>(
    ["task", taskId ?? "none"],
    async (bandId) => {
      const { data, error, response } = await api.GET("/api/v1/bands/{bandId}/tasks/{taskId}", {
        params: { path: { bandId, taskId: taskId as string } },
      });
      if (!response.ok || !data?.data) throw new ApiError(response.status, error);
      return data.data;
    },
    { enabled: taskId != null && taskId !== "" && taskId !== "new", staleTime: 15_000 },
  );
}

// ---- mutations -----------------------------------------------------------------------------------------

/** `POST /bands/{bandId}/tasks` — create a task → 201 TaskDetail. Server-confirm THEN repaint (pending).*/
export function useCreateTask() {
  const { api } = useApp();
  const { bandId } = useBandContext();
  const qc = useQueryClient();

  return useBandMutation<TaskDetail, SaveTaskRequest>(
    async (band, body) => {
      const { data, error, response } = await api.POST("/api/v1/bands/{bandId}/tasks", {
        params: { path: { bandId: band } },
        body,
      });
      // No phantom success: a non-2xx or a body without the created task throws → toast.
      if (!response.ok || !data?.data) throw new ApiError(response.status, error);
      return data.data;
    },
    {
      onSuccess: () => {
        void qc.invalidateQueries({ queryKey: tasksPrefix(bandId) });
        invalidateTaskBoards(qc, bandId);
        void qc.invalidateQueries({ queryKey: ["me", "tasks"] });
        // A release-scoped task changes the composed release's To-do counts (Overview KPI / hub %). Refresh
        // open command-center records too — coarse + cheap (only mounted release queries refetch), .
        void qc.invalidateQueries({ queryKey: ["band", bandId, "release"] });
      },
      onError: (err) => pushToast(errorMessage(err, "Couldn't create task")),
    },
  );
}

/**
 * `PUT /bands/{bandId}/tasks/{taskId}` — edit a task's fields. Sends `If-Match: "<version>"` (409 on a
 * stale version). Server-confirm THEN repaint — the editor surfaces the error verbatim.
 *
 * The body is a FULL REPLACEMENT of the drawer's editable surface (`UpdateTaskRequest`), so a null CLEARS —
 * that is what lets someone empty a due date. Build it from the current detail plus the one field being
 * changed; never send a partial.
 *
 * The response is written straight into the detail cache before the invalidate, so the NEXT edit already
 * holds the bumped version. Without that, a second field committed before the refetch lands would send the
 * stale If-Match and 409 — which reads to the user as "the drawer randomly refuses my second edit".
 */
export function useUpdateTask() {
  const { api } = useApp();
  const { bandId } = useBandContext();
  const qc = useQueryClient();

  return useBandMutation<TaskDetail, { taskId: string; version: number; body: UpdateTaskRequest }>(
    async (band, { taskId, version, body }) => {
      const { data, error, response } = await api.PUT("/api/v1/bands/{bandId}/tasks/{taskId}", {
        params: { path: { bandId: band, taskId } },
        headers: { "If-Match": `"${version}"` },
        body,
      });
      if (!response.ok || !data?.data) throw new ApiError(response.status, error);
      return data.data;
    },
    {
      onSuccess: (res, vars) => {
        qc.setQueryData(taskKey(bandId, vars.taskId), res);
        void qc.invalidateQueries({ queryKey: taskKey(bandId, vars.taskId) });
        void qc.invalidateQueries({ queryKey: tasksPrefix(bandId) });
        invalidateTaskBoards(qc, bandId);
        void qc.invalidateQueries({ queryKey: ["band", bandId, "release"] });
      },
      onError: (err) => pushToast(errorMessage(err, "Couldn't save changes")),
    },
  );
}

/** `DELETE /bands/{bandId}/tasks/{taskId}` → 204. 403 if not the initiator / an AV — surfaced verbatim.*/
export function useDeleteTask() {
  const { api } = useApp();
  const { bandId } = useBandContext();
  const qc = useQueryClient();

  return useBandMutation<void, string>(
    async (band, taskId) => {
      const { error, response } = await api.DELETE("/api/v1/bands/{bandId}/tasks/{taskId}", {
        params: { path: { bandId: band, taskId } },
      });
      if (!response.ok) throw new ApiError(response.status, error);
    },
    {
      onSuccess: () => {
        void qc.invalidateQueries({ queryKey: tasksPrefix(bandId) });
        invalidateTaskBoards(qc, bandId);
        void qc.invalidateQueries({ queryKey: ["me", "tasks"] });
        void qc.invalidateQueries({ queryKey: ["band", bandId, "release"] });
      },
      onError: (err) => pushToast(errorMessage(err, "Couldn't delete task")),
    },
  );
}

/**
 * `POST /bands/{bandId}/tasks/{taskId}/transition` ({toStatus, notes?}) → TaskDetail. Sends
 * `If-Match: "<version>"`. OPTIMISTIC: patch the status in the detail + every list row immediately,
 * roll back + toast on error (422 illegal transition / 403 non-initiator approve / 409 stale — all verbatim),
 * invalidate on settled. NEVER reports success on an unpersisted write.
 */
export function useTransitionTask() {
  const { api } = useApp();
  const { bandId } = useBandContext();
  const qc = useQueryClient();

  return useBandMutation<
    TaskDetail,
    { taskId: string; toStatus: string; version: number; notes?: string | null },
    { prevDetail?: TaskDetail; prevLists: [readonly unknown[], unknown][] }
  >(
    async (band, { taskId, toStatus, version, notes }) => {
      const { data, error, response } = await api.POST("/api/v1/bands/{bandId}/tasks/{taskId}/transition", {
        params: { path: { bandId: band, taskId } },
        headers: { "If-Match": `"${version}"` },
        body: { toStatus, notes: notes ?? null },
      });
      if (!response.ok || !data?.data) throw new ApiError(response.status, error);
      return data.data;
    },
    {
      onMutate: async ({ taskId, toStatus }) => {
        const detailK = taskKey(bandId, taskId);
        const prefix = tasksPrefix(bandId);
        await qc.cancelQueries({ queryKey: detailK });
        await qc.cancelQueries({ queryKey: prefix });
        const prevDetail = qc.getQueryData<TaskDetail>(detailK);
        const prevLists = qc.getQueriesData({ queryKey: prefix }) as [readonly unknown[], unknown][];
        // Patch the detail…
        if (prevDetail) qc.setQueryData<TaskDetail>(detailK, { ...prevDetail, status: toStatus });
        // …and every list row for this task (the widget repaints its pill instantly).
        qc.setQueriesData<TaskListRead>({ queryKey: prefix }, (old) =>
          old
            ? { items: (old.items ?? []).map((t) => (t.taskId === taskId ? { ...t, status: toStatus } : t)) }
            : old,
        );
        return { prevDetail, prevLists };
      },
      onError: (err, { taskId }, ctx) => {
        restore(qc, taskKey(bandId, taskId), ctx?.prevDetail, ctx?.prevLists ?? []);
        pushToast(errorMessage(err, "Couldn't update the task"));
      },
      onSettled: (_res, _err, { taskId }) => {
        void qc.invalidateQueries({ queryKey: taskKey(bandId, taskId) });
        void qc.invalidateQueries({ queryKey: tasksPrefix(bandId) });
        invalidateTaskBoards(qc, bandId);
        void qc.invalidateQueries({ queryKey: ["me", "tasks"] });
        void qc.invalidateQueries({ queryKey: ["band", bandId, "release"] });
      },
    },
  );
}

/**
 * `POST /bands/{bandId}/tasks/{taskId}/assign` ({personId}) → TaskDetail. OPTIMISTIC: append
 * the assignee to the detail immediately, roll back + toast on error (422 if the person doesn't reach the
 * band), invalidate on settled. Lists repaint on the settled refetch.
 */
export function useAssignTask() {
  const { api } = useApp();
  const { bandId } = useBandContext();
  const qc = useQueryClient();
  // The optimistic row's ASSIGNER is the caller — you are the one doing the assigning. It used to be null,
  // which the To-do card resolves to the task's CREATOR (ART-120), so between the click and the settled
  // refetch the row credited whoever wrote the task with an assignment you had just made.
  const myPersonId = useMe().data?.person?.personId ?? null;

  return useBandMutation<TaskDetail, { taskId: string; body: AssignRequest }, { prevDetail?: TaskDetail }>(
    async (band, { taskId, body }) => {
      const { data, error, response } = await api.POST("/api/v1/bands/{bandId}/tasks/{taskId}/assign", {
        params: { path: { bandId: band, taskId } },
        body,
      });
      if (!response.ok || !data?.data) throw new ApiError(response.status, error);
      return data.data;
    },
    {
      onMutate: async ({ taskId, body }) => {
        const key = taskKey(bandId, taskId);
        await qc.cancelQueries({ queryKey: key });
        const prevDetail = qc.getQueryData<TaskDetail>(key);
        if (prevDetail) {
          const optimistic: TaskAssigneeDto = {
            personId: body.personId ?? null,
            assignedByPersonId: myPersonId,
            assignedOn: new Date().toISOString(),
          };
          qc.setQueryData<TaskDetail>(key, { ...prevDetail, assignees: [...(prevDetail.assignees ?? []), optimistic] });
        }
        return { prevDetail };
      },
      onError: (err, { taskId }, ctx) => {
        if (ctx?.prevDetail) qc.setQueryData(taskKey(bandId, taskId), ctx.prevDetail);
        pushToast(errorMessage(err, "Couldn't assign the task"));
      },
      onSettled: (_res, _err, { taskId }) => {
        void qc.invalidateQueries({ queryKey: taskKey(bandId, taskId) });
        void qc.invalidateQueries({ queryKey: tasksPrefix(bandId) });
        invalidateTaskBoards(qc, bandId);
      },
    },
  );
}

/**
 * `DELETE /bands/{bandId}/tasks/{taskId}/assign/{personId}` → TaskDetail. OPTIMISTIC, like its assign twin:
 * drop the row immediately, roll back + toast on error, invalidate on settled. Idempotent server-side, so a
 * double-click cannot 404 the drawer. Also busts ['me','tasks'] — the removed person's own panel must lose it.
 */
export function useUnassignTask() {
  const { api } = useApp();
  const { bandId } = useBandContext();
  const qc = useQueryClient();

  return useBandMutation<TaskDetail, { taskId: string; personId: string }, { prevDetail?: TaskDetail }>(
    async (band, { taskId, personId }) => {
      const { data, error, response } = await api.DELETE("/api/v1/bands/{bandId}/tasks/{taskId}/assign/{personId}", {
        params: { path: { bandId: band, taskId, personId } },
      });
      if (!response.ok || !data?.data) throw new ApiError(response.status, error);
      return data.data;
    },
    {
      onMutate: async ({ taskId, personId }) => {
        const key = taskKey(bandId, taskId);
        await qc.cancelQueries({ queryKey: key });
        const prevDetail = qc.getQueryData<TaskDetail>(key);
        if (prevDetail)
          qc.setQueryData<TaskDetail>(key, {
            ...prevDetail,
            assignees: (prevDetail.assignees ?? []).filter((a) => a.personId !== personId),
          });
        return { prevDetail };
      },
      onError: (err, { taskId }, ctx) => {
        if (ctx?.prevDetail) qc.setQueryData(taskKey(bandId, taskId), ctx.prevDetail);
        pushToast(errorMessage(err, "Couldn't remove the assignee"));
      },
      onSettled: (_res, _err, { taskId }) => {
        void qc.invalidateQueries({ queryKey: taskKey(bandId, taskId) });
        void qc.invalidateQueries({ queryKey: tasksPrefix(bandId) });
        invalidateTaskBoards(qc, bandId);
        void qc.invalidateQueries({ queryKey: ["me", "tasks"] });
      },
    },
  );
}

/**
 * `PUT /bands/{bandId}/tasks/{taskId}/subtasks` ({items}) → TaskDetail. Sends `If-Match`, because ticking the
 * last box can move the task's STATUS (the server's auto-complete rule) — this is a status mutation wearing a
 * checkbox, not a field edit.
 *
 * OPTIMISTIC on the LIST only: the checkbox flips instantly (a checkbox that waits on a round trip reads as
 * broken), while the derived status, the completion stamp and the activity entry arrive with the response.
 * The client deliberately does NOT predict the auto-complete — the rule has exemptions (`needs_review`,
 * `approved`) and a client that guessed them would show a task completing itself and then take it back.
 */
export function useSetSubtasks() {
  const { api } = useApp();
  const { bandId } = useBandContext();
  const qc = useQueryClient();

  return useBandMutation<
    TaskDetail,
    { taskId: string; version: number; items: TaskSubtaskInput[] },
    { prevDetail?: TaskDetail }
  >(
    async (band, { taskId, version, items }) => {
      const { data, error, response } = await api.PUT("/api/v1/bands/{bandId}/tasks/{taskId}/subtasks", {
        params: { path: { bandId: band, taskId } },
        headers: { "If-Match": `"${version}"` },
        body: { items },
      });
      if (!response.ok || !data?.data) throw new ApiError(response.status, error);
      return data.data;
    },
    {
      onMutate: async ({ taskId, items }) => {
        const key = taskKey(bandId, taskId);
        await qc.cancelQueries({ queryKey: key });
        const prevDetail = qc.getQueryData<TaskDetail>(key);
        if (prevDetail)
          qc.setQueryData<TaskDetail>(key, {
            ...prevDetail,
            subtasks: items.map((i) => ({ id: i.id ?? null, title: i.title ?? "", done: i.done ?? false })),
          });
        return { prevDetail };
      },
      onSuccess: (res, vars) => {
        // The server's copy wins immediately (ids for new rows, the derived status, the completion stamp) and
        // carries the bumped version for the next tick.
        qc.setQueryData(taskKey(bandId, vars.taskId), res);
      },
      onError: (err, { taskId }, ctx) => {
        if (ctx?.prevDetail) qc.setQueryData(taskKey(bandId, taskId), ctx.prevDetail);
        pushToast(errorMessage(err, "Couldn't save the checklist"));
      },
      onSettled: (_res, _err, { taskId }) => {
        void qc.invalidateQueries({ queryKey: taskKey(bandId, taskId) });
        void qc.invalidateQueries({ queryKey: tasksPrefix(bandId) });
        invalidateTaskBoards(qc, bandId);
        void qc.invalidateQueries({ queryKey: ["me", "tasks"] });
        void qc.invalidateQueries({ queryKey: ["band", bandId, "release"] });
      },
    },
  );
}

/** `POST /bands/{bandId}/tasks/{taskId}/comments` ({body}) → 201 TaskCommentDto. Pending-state; invalidate detail. */
export function useAddTaskComment() {
  const { api } = useApp();
  const { bandId } = useBandContext();
  const qc = useQueryClient();

  return useBandMutation<TaskCommentDto, { taskId: string; body: string }>(
    async (band, { taskId, body }) => {
      const { data, error, response } = await api.POST("/api/v1/bands/{bandId}/tasks/{taskId}/comments", {
        params: { path: { bandId: band, taskId } },
        body: { body },
      });
      if (!response.ok || !data?.data) throw new ApiError(response.status, error);
      return data.data;
    },
    {
      onSuccess: (_res, vars) => {
        void qc.invalidateQueries({ queryKey: taskKey(bandId, vars.taskId) });
      },
      onError: (err) => pushToast(errorMessage(err, "Couldn't post comment")),
    },
  );
}
